import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Check, Edit, Loader2, Minus, Plus, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { ALL_PLAN_FEATURES, PLAN_FEATURE_LABELS, type PlanFeature } from "@shared/planFeatures";

const fmt = (n: number) => n >= 999_000 ? "Ilimitado" : n.toLocaleString("pt-BR");
const parsePrice = (price: string | null | undefined): number => {
  if (!price) return 0;
  const m = price.match(/(\d+[.,]?\d*)/);
  return m ? Number(m[1].replace(",", ".")) : 0;
};

export function PlansTab() {
  const { data: plans, refetch } = trpc.superAdmin.listPlans.useQuery();
  const { data: tenants } = trpc.superAdmin.listTenants.useQuery();

  const upsertMut = trpc.superAdmin.upsertPlan.useMutation({
    onSuccess: () => { toast.success("Plano salvo"); refetch(); setEditing(null); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const deleteMut = trpc.superAdmin.deletePlan.useMutation({
    onSuccess: () => { toast.success("Plano excluído"); refetch(); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const [editing, setEditing] = useState<{
    key: string; name: string; price: string;
    maxContacts: number; maxMessagesPerMonth: number; maxUsers: number; maxFlows: number; maxSocialAccounts: number;
    features: string[]; stripePriceId: string;
  } | null>(null);

  const alternarFeature = (feature: PlanFeature) => setEditing(e => e && ({
    ...e,
    features: e.features.includes(feature)
      ? e.features.filter(f => f !== feature)
      : [...e.features, feature],
  }));

  const tenantsByPlan = (planKey: string) => tenants?.filter(t => t.plan?.key === planKey) ?? [];

  const totalMrr = (plans ?? []).reduce((acc, p) => {
    const ts = tenantsByPlan(p.key).filter(t => t.status === "active");
    return acc + parsePrice(p.price) * ts.length;
  }, 0);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Planos & Receita</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure os planos oferecidos. MRR estimado é calculado por plano ativo × preço.
          </p>
        </div>
        <Button size="sm" onClick={() => setEditing({ key: "", name: "", price: "", maxContacts: 500, maxMessagesPerMonth: 1000, maxUsers: 2, maxFlows: 5, maxSocialAccounts: 1, features: [], stripePriceId: "" })}>
          <Plus className="w-4 h-4 mr-1.5" /> Novo Plano
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">MRR estimado</p>
            <p className="text-2xl font-bold mt-1">{totalMrr.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {(plans ?? []).map(p => {
          const ts = tenantsByPlan(p.key);
          const active = ts.filter(t => t.status === "active").length;
          const monthly = parsePrice(p.price);
          return (
            <Card key={p.id} className="group relative overflow-hidden">
              {p.key === "enterprise" && (
                <div className="absolute top-3 right-3"><Zap className="w-4 h-4 text-amber-500" /></div>
              )}
              <CardContent className="pt-5 pb-4">
                <Badge variant="outline" className="text-[10px] mb-2">{p.name}</Badge>
                <p className="text-2xl font-bold">{p.price}</p>
                <div className="mt-4 space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Contatos</span><span className="font-medium">{fmt(p.maxContacts)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Msgs/mês</span><span className="font-medium">{fmt(p.maxMessagesPerMonth)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Usuários</span><span className="font-medium">{fmt(p.maxUsers)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Fluxos</span><span className="font-medium">{fmt(p.maxFlows)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Contas sociais</span><span className="font-medium">{fmt(p.maxSocialAccounts)}</span></div>
                </div>
                {/* O que o plano libera, e o que NÃO libera. Mostrar só o que
                    tem esconderia a pergunta que o admin realmente faz aqui —
                    "por que o cliente não consegue usar o Assistente?". */}
                <div className="mt-3 space-y-1 text-[11px]">
                  {ALL_PLAN_FEATURES.map(f => {
                    const tem = (p.features ?? []).includes(f);
                    return (
                      <div key={f} className={tem ? "flex items-start gap-1.5" : "flex items-start gap-1.5 text-muted-foreground"}>
                        {tem
                          ? <Check className="w-3 h-3 mt-0.5 shrink-0 text-emerald-600" />
                          : <Minus className="w-3 h-3 mt-0.5 shrink-0 opacity-50" />}
                        <span className="leading-tight">{PLAN_FEATURE_LABELS[f].label}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-3 border-t flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold">{ts.length}</p>
                    <p className="text-[10px] text-muted-foreground">{active} ativa(s) · MRR {(monthly * active).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditing({
                      key: p.key, name: p.name, price: p.price,
                      maxContacts: p.maxContacts, maxMessagesPerMonth: p.maxMessagesPerMonth,
                      maxUsers: p.maxUsers, maxFlows: p.maxFlows, maxSocialAccounts: p.maxSocialAccounts,
                      features: p.features ?? [],
                      stripePriceId: p.stripePriceId ?? "",
                    })}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600"
                      onClick={() => {
                        if (ts.length > 0) { toast.error(`Não é possível excluir: ${ts.length} empresa(s) usando este plano.`); return; }
                        if (confirm(`Excluir plano "${p.name}"?`)) deleteMut.mutate({ id: p.id });
                      }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing?.key ? `Editar — ${editing.name}` : "Novo Plano"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Chave (slug)</Label>
                  <Input value={editing.key} onChange={e => setEditing({ ...editing, key: e.target.value })} placeholder="ex: starter" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nome</Label>
                  <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="Starter" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Preço (texto livre)</Label>
                <Input value={editing.price} onChange={e => setEditing({ ...editing, price: e.target.value })} placeholder="R$ 97/mês" />
                <p className="text-[11px] text-muted-foreground">Só o texto que aparece na tela. Quem cobra é o Stripe, abaixo.</p>
              </div>
              {/* Sem este campo o plano aparece na vitrine mas ninguém
                  consegue assiná-lo: o checkout recusa plano sem Price. */}
              <div className="space-y-1">
                <Label className="text-xs">Price do Stripe</Label>
                <Input
                  value={editing.stripePriceId}
                  onChange={e => setEditing({ ...editing, stripePriceId: e.target.value })}
                  placeholder="price_1A2b3C..."
                />
                <p className="text-[11px] text-muted-foreground">
                  {editing.key === "free"
                    ? "O plano gratuito não precisa de Price."
                    : "Obrigatório em plano pago — copie do painel do Stripe (Produtos → Preços)."}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Max Contatos</Label><Input type="number" value={editing.maxContacts} onChange={e => setEditing({ ...editing, maxContacts: Number(e.target.value) })} /></div>
                <div className="space-y-1"><Label className="text-xs">Max Msgs/mês</Label><Input type="number" value={editing.maxMessagesPerMonth} onChange={e => setEditing({ ...editing, maxMessagesPerMonth: Number(e.target.value) })} /></div>
                <div className="space-y-1"><Label className="text-xs">Max Usuários</Label><Input type="number" value={editing.maxUsers} onChange={e => setEditing({ ...editing, maxUsers: Number(e.target.value) })} /></div>
                <div className="space-y-1"><Label className="text-xs">Max Fluxos</Label><Input type="number" value={editing.maxFlows} onChange={e => setEditing({ ...editing, maxFlows: Number(e.target.value) })} /></div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Max Contas sociais</Label>
                  <Input type="number" min={0} value={editing.maxSocialAccounts} onChange={e => setEditing({ ...editing, maxSocialAccounts: Number(e.target.value) })} />
                  <p className="text-[10px] text-muted-foreground">Páginas do Facebook conectadas ao mesmo tempo. Cada Página com o Instagram dela conta como uma.</p>
                </div>
              </div>
              <div className="space-y-2 border-t pt-3">
                <Label className="text-xs">Recursos incluídos</Label>
                {ALL_PLAN_FEATURES.map(f => (
                  <div key={f} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-medium leading-tight">{PLAN_FEATURE_LABELS[f].label}</p>
                      <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{PLAN_FEATURE_LABELS[f].description}</p>
                    </div>
                    <Switch
                      checked={editing.features.includes(f)}
                      onCheckedChange={() => alternarFeature(f)}
                      aria-label={PLAN_FEATURE_LABELS[f].label}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button disabled={upsertMut.isPending || !editing?.key || !editing?.name || !editing?.price}
              onClick={() => editing && upsertMut.mutate(editing)}>
              {upsertMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
